import React, { useState } from 'react';
import { getAuth, deleteUser } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import PhoneAuth from '../PhoneAuth';
import { AlertTriangle, Trash2, CheckCircle, LogOut } from 'lucide-react';

const BRAND_COLORS = {
    primary: '#1a1a1a',
    secondary: '#7C3AED',
    accent: '#C9A961',
    light: '#F5F5F0',
    lightGray: '#E8E8E3',
    red: '#DC2626',
};

const DeleteProfilePage = ({ user }) => {
    const navigate = useNavigate();
    const [showSignIn, setShowSignIn] = useState(false);

    const handleDeleteAccount = async () => {
        if (window.confirm("Are you sure you want to delete your account? This action is permanent and cannot be undone. All your data including profile, jobs, and applications will be permanently removed.")) {
            try {
                const auth = getAuth();
                const currentUser = auth.currentUser;
                if (currentUser) {
                    await deleteUser(currentUser);
                    alert("Your account has been successfully deleted.");
                    navigate('/');
                }
            } catch (error) {
                console.error("Error deleting account:", error);
                if (error.code === 'auth/requires-recent-login') {
                    alert("For security reasons, please log out and log back in to verify your identity before deleting your account.");
                } else {
                    alert("Failed to delete account. Please try again.");
                }
            }
        }
    };

    const handleSignOut = () => {
        const auth = getAuth();
        auth.signOut().then(() => {
            // State update handled by App.js listener
        });
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 md:p-8" style={{ backgroundColor: BRAND_COLORS.light }}>

            {/* Header / Logo */}
            <div className="mb-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 border-2 mb-4" style={{ borderColor: BRAND_COLORS.accent }}>
                    <span className="text-3xl font-light" style={{ color: BRAND_COLORS.accent }}>S</span>
                    <span className="text-xs font-light translate-y-1 ml-0.5" style={{ color: BRAND_COLORS.accent }}>V</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-light tracking-widest" style={{ color: BRAND_COLORS.primary }}>
                    SKILL<span className="font-extralight">VEND</span>
                </h1>
                <p className="text-xs tracking-[0.3em] text-gray-500 mt-2 uppercase">Account Deletion</p>
            </div>

            <div className="w-full max-w-lg bg-white border-2 p-8 md:p-12 shadow-sm" style={{ borderColor: BRAND_COLORS.primary }}>

                {!user ? (
                    // NOT LOGGED IN STATE
                    !showSignIn ? (
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Trash2 className="w-10 h-10 text-gray-400" />
                            </div>
                            <h2 className="text-2xl font-light mb-4" style={{ color: BRAND_COLORS.primary }}>Delete Account</h2>
                            <p className="text-gray-600 font-light leading-relaxed mb-8">
                                To delete your SkillVend account and associated data, you must first sign in to verify your identity.
                            </p>
                            <button
                                onClick={() => setShowSignIn(true)}
                                className="w-full py-4 px-6 bg-black text-white font-light tracking-widest text-sm transition-all hover:bg-opacity-90"
                            >
                                SIGN IN TO DELETE
                            </button>
                            <p className="text-xs text-gray-400 mt-6 leading-relaxed">
                                If you deleted the app but want to remove your data, please sign in with your registered phone number.
                            </p>
                        </div>
                    ) : (
                        <div className="animate-fade-in-up">
                            <div className="mb-6">
                                <button
                                    onClick={() => setShowSignIn(false)}
                                    className="text-xs uppercase tracking-widest text-gray-500 hover:text-black mb-4 flex items-center"
                                >
                                    ← Back
                                </button>
                            </div>
                            <PhoneAuth
                                isSignUp={false}
                                role={null}
                                onSuccess={() => {
                                    // Auth listener in App.js will update 'user' prop, re-rendering this component
                                    setShowSignIn(false);
                                }}
                                onBack={() => setShowSignIn(false)}
                            />
                        </div>
                    )
                ) : (
                    // LOGGED IN STATE
                    <div className="text-center">
                        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-red-100">
                            <AlertTriangle className="w-10 h-10 text-red-600" />
                        </div>

                        <h2 className="text-2xl font-light mb-2" style={{ color: BRAND_COLORS.primary }}>Delete Account?</h2>
                        <div className="bg-gray-50 px-4 py-2 rounded-full inline-block mb-6">
                            <p className="text-sm font-medium text-gray-600">{user.phoneNumber}</p>
                        </div>

                        <div className="text-left bg-red-50 p-6 border border-red-100 mb-8 rounded-sm">
                            <h3 className="text-red-800 font-medium mb-3 text-sm uppercase tracking-wide">Warning: Permanent Action</h3>
                            <ul className="space-y-2 text-sm text-red-700 font-light">
                                <li className="flex items-start">• All personal profile data will be erased.</li>
                                <li className="flex items-start">• Job history and active applications will be removed.</li>
                                <li className="flex items-start">• Verification documents will be deleted.</li>
                                <li className="flex items-start">• This action <span className="font-semibold ml-1">cannot be undone</span>.</li>
                            </ul>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={handleDeleteAccount}
                                className="w-full py-4 px-6 bg-red-600 text-white font-medium tracking-widest text-sm transition-all hover:bg-red-700 shadow-md"
                            >
                                DELETE MY ACCOUNT PERMANENTLY
                            </button>

                            <button
                                onClick={handleSignOut}
                                className="w-full py-4 px-6 border border-gray-200 text-gray-500 font-light tracking-widest text-sm transition-all hover:text-black hover:border-black"
                            >
                                CANCEL & SIGN OUT
                            </button>
                        </div>
                    </div>
                )}

            </div>

            {/* Footer minimal */}
            <div className="mt-12 text-center text-gray-400">
                <p className="text-[10px] tracking-widest uppercase">SkillVend Security</p>
            </div>
        </div>
    );
};

export default DeleteProfilePage;
